import Edit from '@rover/icons/svg/edit.svg';
import Map from '@rover/icons/svg/map.svg';
import NowBolt from '@rover/icons/svg/now-bolt.svg';
import Person from '@rover/icons/svg/person.svg';
import Search from '@rover/icons/svg/search.svg';
import { Icon } from '@rover/icons/utils';
import TextWithSingleInlineLink from '@rover/kibble/official-patterns/TextWithSingleInlineLink';
import { isTaggedParagraphText } from '@rover/kibble/official-patterns/TextWithSingleInlineLink/TextWithSingleInlineLink.common';
import { AlertSeverity } from '@rover/react-lib/src/components/notifications/Alert/Alert';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import { AnalyticsEvent } from '@rover/types/src/Analytics';

import { HeaderIconEnum } from './builders/constants';

export type SearchSectionHeaderProps = {
  id?: string;
  headerIconName?: string;
  headerText: string;
  infoIconOnClick?: () => void;
  primarySubheaderText?: string;
  secondarySubheaderText?: string;
  alertText?: string;
  alertSeverity?: AlertSeverity;
  alertLinkHref?: string;
  alertLinkEvent?: AnalyticsEvent;
};

export const getIcon = (iconName: SearchSectionHeaderProps['headerIconName']): Icon => {
  if (!iconName) {
    return Search as unknown as Icon;
  }

  if (typeof iconName === 'string') {
    switch (iconName) {
      case HeaderIconEnum.EDIT:
        return Edit as unknown as Icon;
      case HeaderIconEnum.MAP:
        return Map as unknown as Icon;
      case HeaderIconEnum.BOLT:
        return NowBolt as unknown as Icon;
      case HeaderIconEnum.PERSON:
        return Person as unknown as Icon;
      case HeaderIconEnum.SEARCH:
      default:
        return Search as unknown as Icon;
    }
  }

  return iconName;
};

export const getAlertElement = (
  alertText: SearchSectionHeaderProps['alertText'],
  alertLinkHref: SearchSectionHeaderProps['alertLinkHref'],
  alertLinkEvent: SearchSectionHeaderProps['alertLinkEvent'],
  getTextElementFunction: (string) => React.ReactNode
): React.ReactNode => {
  if (!alertText) {
    return null;
  }

  if (!isTaggedParagraphText(alertText)) {
    // Text does not have link
    return getTextElementFunction(alertText);
  }

  const onAlertLinkClick = (): void => {
    if (alertLinkEvent) {
      emitAnalyticsEvent(alertLinkEvent);
    }
  };

  // Text has link. Note: Markdown is not supported
  return (
    <TextWithSingleInlineLink
      size="100"
      textColor="secondary"
      target="_blank"
      asParagraph
      paragraphText={alertText}
      href={alertLinkHref || ''}
      onClick={onAlertLinkClick}
      mb="0x"
    />
  );
};

/**
 * Helper function to build the test ID. This allows us to more easily pull the
 * header based on the correct icon and type of header.
 */
export const buildTestId = (
  id: SearchSectionHeaderProps['id'],
  iconName: SearchSectionHeaderProps['headerIconName']
): string => {
  if (!id) {
    return 'search-results-header';
  }

  if (iconName) {
    return `${id}-${iconName.toLowerCase()}`;
  }

  return id;
};
